/**
 * My apps — the apps a person keeps on /apps, picked from the catalog
 * (/apps/catalog).
 *
 * The catalog is every app the person may open (useAccessibleApps: module,
 * flag, entitlement, workspace kind). My apps is the part of it they keep:
 * their own choice for an app when they made one, else the app's registry
 * default (`inMyAppsByDefault`, the small starter set). Choices are kept on
 * the server (get_my_apps / set_my_app, migration 1064), one switch per app,
 * so they follow the person to every device; only what they chose is stored.
 *
 * Keeping an app is not access: taking one off My apps leaves it open from
 * the catalog, and keeping one the person may not open shows nothing.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { getTypedSupabaseClient } from '@/lib/typedSupabaseClient';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { AppDefinition } from './registry';
import { useAccessibleApps } from './RequireAppAccess';

/** A person's own choices: app id → kept. An app not named follows its default. */
export type MyAppChoices = Record<string, boolean>;

export function parseMyAppChoices(raw: unknown): MyAppChoices {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: MyAppChoices = {};
  for (const [id, kept] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof kept === 'boolean') out[id] = kept;
  }
  return out;
}

export function isInMyApps(app: AppDefinition, choices: MyAppChoices): boolean {
  return choices[app.appId] ?? app.inMyAppsByDefault === true;
}

/** The apps a person keeps, in catalog order. */
export function resolveMyApps(apps: readonly AppDefinition[], choices: MyAppChoices): AppDefinition[] {
  return apps.filter((app) => isInMyApps(app, choices));
}

type Rpc = (fn: string, args?: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;

function rpc(): Rpc {
  const client = getTypedSupabaseClient() as unknown as { rpc: Rpc };
  return client.rpc.bind(client);
}

interface MyAppsState {
  choices: MyAppChoices;
  /** Whose choices are loaded; null until the first read answers. */
  loadedFor: string | null;
  error: string | null;
  load: (userId: string) => Promise<void>;
  setKept: (appId: string, kept: boolean) => Promise<void>;
}

export const useMyAppsStore = create<MyAppsState>((set, get) => ({
  choices: {},
  loadedFor: null,
  error: null,
  load: async (userId) => {
    const { data, error } = await rpc()('get_my_apps');
    if (error) {
      // Show the defaults rather than an empty launcher; the next choice retries.
      set({ choices: {}, loadedFor: userId, error: error.message });
      return;
    }
    set({ choices: parseMyAppChoices(data), loadedFor: userId, error: null });
  },
  setKept: async (appId, kept) => {
    const before = get().choices;
    // Move at once; put it back if the server refuses.
    set({ choices: { ...before, [appId]: kept }, error: null });
    const { data, error } = await rpc()('set_my_app', { p_app_id: appId, p_kept: kept });
    if (error) {
      set({ choices: before, error: error.message });
      return;
    }
    set({ choices: parseMyAppChoices(data) });
  },
}));

/**
 * The catalog (every app this person may open here) and the part of it they
 * keep. `ready` once both the gates and the person's choices have answered,
 * so a kept app never appears and then vanishes.
 */
export function useMyApps(): {
  ready: boolean;
  catalog: AppDefinition[];
  myApps: AppDefinition[];
  isKept: (app: AppDefinition) => boolean;
  setKept: (appId: string, kept: boolean) => Promise<void>;
  error: string | null;
} {
  const { ready: gatesReady, apps } = useAccessibleApps();
  const userId = useUserStore((s) => s.userId);
  const { choices, loadedFor, error, load, setKept } = useMyAppsStore();
  const signedIn = Boolean(userId && userId !== 'unknown');

  useEffect(() => {
    if (signedIn && loadedFor !== userId) void load(userId as string);
  }, [signedIn, userId, loadedFor, load]);

  return {
    ready: gatesReady && (!signedIn || loadedFor === userId),
    catalog: apps,
    myApps: resolveMyApps(apps, choices),
    isKept: (app) => isInMyApps(app, choices),
    setKept,
    error,
  };
}
